import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
  Checkbox,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { NotificationManager } from "react-notifications";
import { useDispatch } from "react-redux";
import { clearClients } from "../../../../redux/clientsSlice";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import CommonLoader from "../../common/CommonLoader";
import BatchUpdateClients from "./BatchUpdateClients";
import { DeleteClient, ChangeClientStatusByAccount } from "../../../services/ClientsMaintenanceService";

// ---------------------------------------------------------------------------
// Sorting helpers (same pattern as LabOrdersTable / sortingUtils)
// ---------------------------------------------------------------------------
function descendingComparator(a, b, orderBy) {
  const aVal = a[orderBy] ?? "";
  const bVal = b[orderBy] ?? "";
  if (bVal < aVal) return -1;
  if (bVal > aVal) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const cmp = comparator(a[0], b[0]);
    return cmp !== 0 ? cmp : a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}
// ---------------------------------------------------------------------------

// Columns displayed in the header and the matching data keys
const COLUMNS = [
  { label: "Account Number",  key: "accountNumber" },
  { label: "Client Name",     key: "clientName" },
  { label: "Client Type",     key: "clientType" },
  { label: "Billing Name",    key: "billingName" },
  { label: "Reporting Name",  key: "reportingName" },
  { label: "Shipping Name",   key: "shippingName" },
  { label: "County",          key: "county" },
  { label: "Client Notes",    key: "clientNotes" },
];

const rowsPerPageOptions = [10, 25, 50, 100, 200];

const ClientTable = ({ clientsData, count, onChange, onSearch }) => {
  const [page, setPage]                           = useState(0);
  const [rowsPerPage, setRowsPerPage]             = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm]               = useState("");
  const [filteredData, setFilteredData]           = useState([]);
  const [loading, setLoading]                     = useState(false);
  const dispatch                                  = useDispatch();

  // Sorting
  const [order, setOrder]     = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  // Delete
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedItemToDelete, setSelectedItemToDelete]         = useState(null);

  // Batch update
  const [selectedRows, setSelectedRows]       = useState([]);
  const [isBatchUpdateOpen, setIsBatchUpdateOpen] = useState(false);

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------
  useEffect(() => {
    setSearchTerm("");
    handleChange();
  }, [page, rowsPerPage]);

  useEffect(() => {
    setFilteredData(clientsData);
  }, [clientsData]);

  // -------------------------------------------------------------------------
  // Pagination
  // -------------------------------------------------------------------------
  const handleChangePage        = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };
  const handleChange            = () => onChange(page, rowsPerPage);

  // -------------------------------------------------------------------------
  // Sorting
  // -------------------------------------------------------------------------
  const handleRequestSort = (key) => {
    const isAsc = orderBy === key && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(key);
  };

  const sortedData = orderBy
    ? stableSort(filteredData, getComparator(order, orderBy))
    : filteredData;

  // -------------------------------------------------------------------------
  // Checkbox / row selection
  // -------------------------------------------------------------------------
  const isAllSelected =
    filteredData.length > 0 &&
    filteredData.every((c) => selectedRows.includes(c.accountNumber));

  const handleSelectAll = (e) => {
    setSelectedRows(e.target.checked ? filteredData.map((c) => c.accountNumber) : []);
  };

  const handleRowSelect = (accountNumber) => {
    setSelectedRows((prev) =>
      prev.includes(accountNumber)
        ? prev.filter((id) => id !== accountNumber)
        : [...prev, accountNumber]
    );
  };

  // -------------------------------------------------------------------------
  // Status toggle
  // -------------------------------------------------------------------------
  const handleStatusClick = async (client) => {
    try {
      setLoading(true);
      await ChangeClientStatusByAccount(client.accountNumber);
      // Update the row in place so the user keeps their page, search, sort, and selection
      setFilteredData((prev) =>
        prev.map((c) =>
          c.accountNumber === client.accountNumber ? { ...c, isActive: !c.isActive } : c
        )
      );
      // Reset the cached client list so client selectors reload it with the new status
      dispatch(clearClients());
    } catch {
      NotificationManager.error("Failed to update client status.");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Delete
  // -------------------------------------------------------------------------
  const handleDeleteClick = (client) => {
    setSelectedItemToDelete(client);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => setIsDeleteConfirmationOpen(false);

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteClient(selectedItemToDelete.accountNumber);
      if (response.results === true) {
        NotificationManager.success("Client deleted successfully");
      } else {
        NotificationManager.error("Error deleting client");
      }
    } catch {
      NotificationManager.error("Error deleting client");
    } finally {
      setIsDeleteConfirmationOpen(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------
  const renderCell = (client, key) => {
    if (key === "accountNumber" && client[key]) {
      return (
        <a
          href={`/vlp/edit-client/${client[key]}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#1976d2", textDecoration: "underline" }}
        >
          {client[key]}
        </a>
      );
    }
    if (key === "clientNotes") {
      const notes = client[key] || "";
      return (
        <Tooltip title={notes}>
          <span>{notes.length > 20 ? notes.substring(0, 20) + "…" : notes}</span>
        </Tooltip>
      );
    }
    return client[key];
  };

  const renderActions = (client) => (
    <div style={{ minWidth: "100px" }}>
      <Tooltip title={client.isActive ? "Active — click to deactivate" : "Inactive — click to activate"}>
        <IconButton onClick={() => handleStatusClick(client)}>
          {client.isActive
            ? <CheckCircleIcon style={{ color: "green" }} />
            : <CancelIcon style={{ color: "gray" }} />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton style={{ color: "red" }} onClick={() => handleDeleteClick(client)}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </div>
  );

  const selectedClients = filteredData.filter((c) =>
    selectedRows.includes(c.accountNumber)
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <>
      <Paper style={{ marginRight: "20px" }}>
        {/* Toolbar */}
        <div style={{ display: "flex", padding: "12px 16px", alignItems: "center", gap: "8px" }}>
          <Tooltip title="Search by: Account Number, Client Name, Client Type, Billing Name, Reporting Name, Shipping Name, County, Client Notes">
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onSearch(searchTerm); }}
              style={{ flex: 1, maxWidth: "60%" }}
            />
          </Tooltip>

          <Button variant="contained" color="secondary" onClick={() => onSearch(searchTerm)}>
            Search
          </Button>

          {selectedRows.length > 1 && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<EditNoteIcon />}
              onClick={() => setIsBatchUpdateOpen(true)}
            >
              Update
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => window.open("/vlp/edit-client/add-new", "_blank")}
          >
            New
          </Button>
        </div>

        {/* Table */}
        <TableContainer>
          <Table sx={{ minWidth: 400 }} size="small">
            <TableHead sx={{ backgroundColor: (theme) => theme.palette.primary.main }}>
              <TableRow>
                {/* Checkbox column — not sortable */}
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={selectedRows.length > 0 && !isAllSelected}
                    onChange={handleSelectAll}
                    style={{ color: "white" }}
                  />
                </TableCell>

                {/* Sortable data columns */}
                {COLUMNS.map(({ label, key }) => (
                  <TableCell key={key} sx={{ color: "white" }}>
                    <TableSortLabel
                      active={orderBy === key}
                      direction={orderBy === key ? order : "asc"}
                      onClick={() => handleRequestSort(key)}
                      sx={{
                        color: "white !important",
                        "& .MuiTableSortLabel-icon": { color: "white !important" },
                      }}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}

                {/* Actions column — not sortable */}
                <TableCell sx={{ color: "white" }}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedData.map((client, index) => (
                <TableRow
                  key={client.accountNumber}
                  selected={selectedRows.includes(client.accountNumber)}
                  sx={{ backgroundColor: index % 2 === 0 ? "rgba(0,0,0,0.04)" : "white" }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRows.includes(client.accountNumber)}
                      onChange={() => handleRowSelect(client.accountNumber)}
                    />
                  </TableCell>

                  {COLUMNS.map(({ key }) => (
                    <TableCell key={key}>{renderCell(client, key)}</TableCell>
                  ))}

                  <TableCell>{renderActions(client)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message={`Are you sure you want to delete ${selectedItemToDelete?.clientName}?`}
        color="red"
      />

      <BatchUpdateClients
        open={isBatchUpdateOpen}
        onClose={() => setIsBatchUpdateOpen(false)}
        selectedClients={selectedClients}
        onSuccess={() => {
          setSelectedRows([]);
          setIsBatchUpdateOpen(false);
        }}
      />

      <CommonLoader loading={loading} />
    </>
  );
};

export default ClientTable;